/**
 * InstallPrompt — banner pro instalaci PWA (Chromium-based prohlížeče).
 *
 * Stav instalovatelnosti (`beforeinstallprompt`) drží sdílený hook
 * `useInstallPrompt` (jeden zdroj pravdy — sdílíme s sekcí na `/about`).
 * Kliknutím se spustí systémový instalační dialog. Banner se ukáže s malým
 * zpožděním, ať nenaskočí hned při načtení; „Teď ne" ho trvale skryje.
 *
 * Pozn.: iOS Safari `beforeinstallprompt` NEpodporuje (tam se instaluje přes
 * „Sdílet → Přidat na plochu"), takže se banner zobrazí jen v Chromium.
 */
import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { useInstallPrompt } from "../lib/use-install-prompt.js";

const DISMISSED_KEY = "bw:install:dismissed";

export function InstallPrompt() {
  const { canInstall, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem(DISMISSED_KEY) === "1",
  );
  // Malé zpoždění, ať banner nenaskočí hned při načtení.
  const [delayPassed, setDelayPassed] = useState(false);

  useEffect(() => {
    if (!canInstall || dismissed) return undefined;
    const timer = setTimeout(() => setDelayPassed(true), 2000);
    return () => clearTimeout(timer);
  }, [canInstall, dismissed]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  };

  if (!canInstall || dismissed || !delayPassed) return null;

  return (
    <div
      role="dialog"
      aria-label="Instalace aplikace Beer Wars"
      className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto"
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <Download size={22} className="text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-foreground">Nainstalovat aplikaci</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Přidej si Beer Wars na plochu — rychlejší spuštění, vlastní okno a notifikace
              jako u nativní appky.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => void promptInstall()}
                className="btn btn-sm px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition-opacity"
              >
                Nainstalovat
              </button>
              <button
                onClick={handleDismiss}
                className="btn btn-sm px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted transition-colors"
              >
                Teď ne
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
